import {
  Component,
  ChangeDetectionStrategy,
  signal,
  inject,
  computed,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule, CurrencyPipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MembershipService } from "../../services/membership.service";
import {
  Member,
  PrepaymentHistory,
  UsageHistory,
} from "../../types/membership";
import { MaterialTableComponent } from "../shared/material-table/material-table.component";
import { SortableHeader } from "../../types/table";
import { TranslatePipe } from "../../pipes/translate.pipe";
import { LanguageService } from "../../services/language.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { PopUpCommonComponent } from "@/src/components/pop-up-common/pop-up-common.component";
import { PopUpCommonEditComponent } from "../pop-up-common-edit/pop-up-common-edit.component";
import { MasterService } from "@/src/services/master.service";
import { AuthenticationService } from "@/src/services/authentication.service";
import { Constants } from "@/src/helpers/constants";
import { formatNumberWithCommas, parseFormattedNumber } from "@/src/helpers/number-format.helper";
import { PaymentMethodSelectorComponent, PaymentMethod } from "../shared/payment-method-selector/payment-method-selector.component";
import { InputNumberModule } from "primeng/inputnumber";
import { ClickOutsideDirective } from "../../helpers/ClickOutsideDirective";
import { InputTextModule } from "primeng/inputtext";
import { KsNetService } from "@/src/services/ksNet.service";
import { CheckboxModule } from "primeng/checkbox";
import { WebSocketService } from "@/src/services/webSocket.service";

@Component({
  selector: "app-membership-management",
  standalone: true,
  imports: [
    CurrencyPipe,
    FormsModule,
    MaterialTableComponent,
    TranslateModule,
    PopUpCommonComponent,
    PopUpCommonEditComponent,
    CommonModule,
    PaymentMethodSelectorComponent,
    InputNumberModule,
    ClickOutsideDirective,
    InputTextModule,
    CheckboxModule
  ],
  templateUrl: "./membership-management.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipManagementComponent {
  // Danh sách thành viên lấy từ API
  members = signal<Member[]>([]);
  memberPrepayments = signal<Member[]>([]);
  prepaymentHistory = signal<PrepaymentHistory[]>([]);
  usageHistory = signal<UsageHistory[]>([]);
  webSocket = inject(WebSocketService)
  membershipService = inject(MembershipService);
  translate = inject(TranslateService);
  masterService = inject(MasterService);
  auth = inject(AuthenticationService);
  ksNet = inject(KsNetService);
  cdr = inject(ChangeDetectorRef)
  P_PAGEROW = Constants.P_PAGEROW;


  // Main Tab State
  activeTab = signal<"CUSTOMER" | "PREPAYMENT">("CUSTOMER");

  // Customer Tab State
  searchPhone = signal("");
  searchName = signal("");
  isModalOpen = signal(false);
  newMemberName = signal("");
  newMemberId = signal("");
  newMemberPhone = signal("");
  memberIdEdit = signal("");
  newMemberSms = signal("Y");
  phoneError = signal("");
  selectedRows = signal<string[]>([]);
  easyPayments = signal([]);
  ontoggle = signal(false);

  // search
  searchPhoneInput = signal("");
  searchNameInput = signal("");
  // delete
  showConfirmDelete = signal(false);
  showConfirmCancel = signal(false);
  idsToDelete = signal<string[]>([]);
  // Popup edit chung
  showEdit = false;
  editColumns = [];
  editData: any = {};
  param = {
    P_HQ_CD: "",
    P_STR_CD: "",
    P_HP_NO: "",
    P_USER_NM: "",
  };
  param1 = {
    P_STR_CD: "",
    P_HP_NO: "",
    P_MEMBER_NO: "",
    P_HQ_CD: "",
    P_PAGEINDEX: 0,
    P_PAGEROW: this.P_PAGEROW
  };
  param2 = {
    P_HQ_CD: "",
    P_STR_CD: "",
    P_HP_NO: "",
    P_MEMBER_NO: "",
    P_PAGEINDEX: 0,
    P_PAGEROW: this.P_PAGEROW
  };
  ngOnInit() {
    ((this.param.P_HQ_CD = this.auth.getHQCD()),
      (this.param.P_STR_CD = this.auth.getStrCd()));
    this.getData();
    this.getEasyPayment();

    this.getMemberPrepayment();
  }

  memberChecked = computed(() =>
    this.members().filter(x => x.checked)
  );

  allSelected = computed(() =>
    this.members().length > 0 &&
    this.members().every(x => x.checked)
  );

  // Prepayment Tab State
  historyTab = signal<"PREPAID" | "USAGE">("PREPAID");
  prepaySearchPhone = signal("");
  prepaySearchName = signal("");
  filteredMembersPhone = signal<Member[]>([]);
  filteredMembersName = signal<Member[]>([]);
  showPhoneDropdown = signal(false);
  showNameDropdown = signal(false);
  selectedPrepayCustomer = signal<Member | null>(null);
  prepayChargeAmount = signal<number>(0);
  prepayReturnAmount = signal<number>(0);
  showPaymentMethodSelector = signal(false);
  selectedPaymentMethod = signal<PaymentMethod | null>(null);
  selectedChargeHistory = signal<any>(null)
  paymentType = Constants.paymentType;
  paySuccess = signal<boolean>(false)
  // Computed balance after top up
  balanceAfterTopUp = computed(() => {
    const currentBalance = this.prepaymentHistory()[0]?.remaiN_SAVE_CHARGE ?? 0;
    const topUpAmount = this.prepayChargeAmount() || 0;
    return currentBalance + topUpAmount;
  });

  // Computed balance after return
  balanceAfterReturn = computed(() => {
    const currentBalance = this.selectedPrepayCustomer()?.prepaid ?? 0;
    const returnAmount = this.prepayReturnAmount() || 0;
    return currentBalance - returnAmount;
  });

  deleteMembers() {
    if (!this.memberChecked().length) {
      this.masterService.showError(this.translate.instant("SYSMESER0023"));
      return;
    }
    this.showConfirmDelete.set(true);
  }

  delete() {
    // const selected = this.selectedRows();
    // if (!selected.length) {
    //   this.showConfirmDelete.set(false);
    //   return;
    // }

    const membersData = this.memberChecked().map(member => ({
      MEMBER_NO: member.customerNo,
      MEMBER_ID: member.memberId || "",
      MEMBER_NM: member.name,
      HP_NO: member.phone.replace(/\D/g, ""), // Remove formatting
      SMS_YN: member.smsYn || "Y"
    }));
    // Call batch delete API
    this.membershipService
      .deleteMembers(
        membersData,
        this.auth.getUserId(),
        this.auth.getStrCd()
      )
      .subscribe({
        next: (res) => {
          if (res?.isOK) {
            this.masterService.showSuccess(
              this.translate.instant("SYSMESER0007")
            );
            this.getData();
            this.selectedRows.set([]);
            this.showConfirmDelete.set(false);
          } else {
            this.masterService.showError(
              Constants.showError(res?.errorMessages)
            );
            this.showConfirmDelete.set(false);
          }
        },
        error: (err) => {
          this.masterService.showError(
            this.translate.instant("DELETE_FAILED")
          );
          this.showConfirmDelete.set(false);
        }
      });
  }

  nextPage(page: number) {
    this.getData(page, true);
  }

  totalCnt = signal(0);
  isNextPage = signal(false)
  getData(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.isNextPage.set(isNextPage)
    this.ontoggle.set(false);
    const param = {
      ...this.param,
      P_HP_NO: this.param.P_HP_NO.replaceAll("-", ''),
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_STR_GET_MEMBER_LIST
    this.masterService
      .excute_sp("6a839c6c-298d-11f1-9014-00155d001e03", param)
      .subscribe((res: any) => {
        if (res?.isOK) {
          this.totalCnt.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
          const newData = res?.result?.dataRes[1]?.rows || [];

          const mapped = newData.map((item: any) => ({
            id: item.membeR_NO, // Use MEMBER_NO as unique ID
            checked: false,
            memberId: item.membeR_ID, // Store MEMBER_ID separately
            customerNo: item.membeR_NO,
            name: item.membeR_NM,
            phone: this.formatPhoneNumber(item.hP_NO) || item.hP_NO,
            grade: item.membeR_TP_NM || "",
            acc: item.savE_POINT || 0,
            used: item.usE_POINT || 0,
            avail: item.avaiL_POINT || 0,
            count: item.savE_CNT || 0,
            prepaid: item.savE_CHARGE_AMT || 0,
            joinDate: item.joiN_DT ? this.masterService.formatDateStringYYYYMMDD(item.joiN_DT) : "",
            smsYn: item.smS_YN || "Y",
          }));
          this.members.set(mapped);
        }
      });
  }

  getMemberPrepayment() {
    const param = {
      ...this.param
    }
    //SP_STR_GET_MEMBER_LIST
    this.masterService
      .excute_spNoLoading("6a839c6c-298d-11f1-9014-00155d001e02", param)
      .then((res: any) => {
        if (res?.isOK) {
          const newData = res?.result?.dataRes[0]?.rows || [];
          const mapped = newData.map((item: any) => ({
            id: item.membeR_NO, // Use MEMBER_NO as unique ID
            checked: false,
            memberId: item.membeR_ID, // Store MEMBER_ID separately
            customerNo: item.membeR_NO,
            name: item.membeR_NM,
            phone: this.formatPhoneNumber(item.hP_NO) || item.hP_NO,
            grade: item.membeR_TP_NM || "",
            acc: item.savE_POINT || 0,
            used: item.usE_POINT || 0,
            avail: item.avaiL_POINT || 0,
            count: item.savE_CNT || 0,
            prepaid: item.savE_CHARGE_AMT || 0,
            joinDate: item.joiN_DT ? this.masterService.formatDateStringYYYYMMDD(item.joiN_DT) : "",
            smsYn: item.smS_YN || "Y",
          }));
          this.memberPrepayments.set(mapped);
        }
      });
  }

  getEasyPayment() {
    this.masterService.getCommNoLoading('ST28').then((res: any) => {
      if (res?.isOK) {
        this.easyPayments.set(res?.result?.dataRes[0]?.rows);
      }
    })
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return "";
    // If already in yyyy-mm-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    // Handle yyyymmdd format (8 digits)
    if (/^\d{8}$/.test(dateStr)) {
      return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }

    // Try parsing as Date object
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    return dateStr;
  }


  nextPageHistory(page: number) {
    this.getDataPrepaymentHistory(page, true);
  }

  totalCntHistory = signal(0);
  isNextPageHistory = signal(false)
  getDataPrepaymentHistory(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    this.isNextPageHistory.set(isNextPage)
    //SP_STR_MEMBER_CHARGE_SAVE
    this.param1.P_PAGEINDEX = P_PAGEINDEX;
    this.masterService
      .excute_sp("6a83a19e-298d-11f1-9014-00155d001e03", this.param1)
      .subscribe((res: any) => {
        if (res?.isOK) {
          const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
          this.totalCntHistory.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
          const newData = res?.result?.dataRes[1]?.rows || [];
          const mapped = newData.map((item: any, index: number) => ({
            seqNo: String(startIndex + index + 1).padStart(2, '0'),
            date: this.formatDate(item.savE_CHARGE_DT || ""),
            receiptNo: item.chargE_SEQ || "",
            category: item.chargE_FG_NM || "",
            topUpAmount: item.savE_CHARGE_AMT || 0,
            balance: item.totaL_CHARGE_AMT || 0,
            cancellable: item.canceL_FG_NM || "",
            canceL_FG: item.canceL_FG || "",
            savE_CHARGE_DT: item.savE_CHARGE_DT || "",
            chargE_SEQ: item.chargE_SEQ || "",
            chargE_FG: item.chargE_FG || "",
            paiD_CHARGE_AMT: item.paiD_CHARGE_AMT,
            savE_CHARGE_AMT: item.chargE_FG,
            totaL_CHARGE_AMT: item.chargE_FG,
            hP_NO: item.hP_NO || "",
            hQ_CD: item.hQ_CD || "",
            stR_CD: item.stR_CD || "",
            membeR_NO: item.membeR_NO || "",
            poS_NO: item.poS_NO || "",
            remaiN_SAVE_CHARGE: item.remaiN_SAVE_CHARGE || 0,
            iS_CANCELED: item.iS_CANCELED

          }));
          this.prepaymentHistory.set(mapped);
        }
      });
  }


  totalCntUse = signal(0);
  isNextPageUse = signal(false)

  nextPageUse(page: number) {
    this.getDataUsageHistory(page, true);
  }

  getDataUsageHistory(P_PAGEINDEX: number = 1, isNextPage: boolean = false) {
    //SP_STR_MEMBER_CHARGE_USE
    this.isNextPageUse.set(isNextPage)
    this.param2.P_PAGEINDEX = P_PAGEINDEX;
    this.masterService
      .excute_sp("6a83a1d3-298d-11f1-9014-00155d001e03", this.param2)
      .subscribe((res: any) => {
        if (res?.isOK) {
          const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
          this.totalCntUse.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
          const newData = res?.result?.dataRes[1]?.rows || [];
          const mapped = newData.map((item: any, index: number) => ({
            seqNo: String(startIndex + index + 1).padStart(2, '0'),
            date: this.formatDate(item.salE_DT || ""),
            receiptNo: item.salE_NO || "",
            usedAmount: item.cahrgE_USE_AMT || 0,
            balance: item.totaL_CHARGE_AMT || 0,
            category: item.salE_TP_NM || "",
          }));
          this.usageHistory.set(mapped);
        }
      });
  }
  searchMembers() {
    // this.param.P_HP_NO = this.param.P_HP_NO;
    // this.param.P_USER_NM = this.param.P_USER_NM;
    this.selectedRows.set([]);
    this.getData();
  }

  resetSearch() {
    this.param.P_HP_NO = "";
    this.param.P_USER_NM = "";
    this.selectedRows.set([]);
    this.getData();
  }

  showError(message: string) {
    alert(message);
  }

  editMember(customerNo?: string) {
    let memberToEdit: Member | undefined;

    if (customerNo) {
      // Double-click case
      memberToEdit = this.members().find((m) => m.customerNo === customerNo);
    } else {
      // Button click case
      const selected = this.selectedRows();
      if (selected.length !== 1) {
        this.masterService.showError(this.translate.instant("MULTI_ROW_EDIT"));
        return;
      }
      memberToEdit = this.members().find((m) => m.id === selected[0]);
    }

    if (!memberToEdit) return;
    this.memberIdEdit.set(memberToEdit.memberId || "")
    this.newMemberName.set(memberToEdit.name);
    this.newMemberId.set(memberToEdit.customerNo);
    this.newMemberPhone.set(memberToEdit.phone);
    this.newMemberSms.set(memberToEdit.smsYn || "Y");
    this.phoneError.set("");
    this.isModalOpen.set(true);
    // this.editData = {
    //   ...memberToEdit,
    //   memberId: memberToEdit.memberId || "",
    //   smsYn: memberToEdit.smsYn || "Y" // Default to Y if not present
    // };
    // this.editColumns = [
    //   { key: "customerNo", captionName: "CUSTMSTR0029", type: "string", disabled: true },
    //   { key: "name", captionName: "CUSTMSTR0017", type: "string", maxLength: 300 },
    //   { key: "phone", captionName: "CUSTMSTR0018", type: "string", requierd: true, maxLength: 20 },
    //   { key: "smsYn", captionName: "CUSTMSTR0019", type: "select", options: [{ value: "Y", label: "CUSTMSTR0064" }, { value: "N", label: "CUSTMSTR0065" }] },
    // ];
    // this.showEdit = true;
  }
  onSaveEdit(edited: any) {
    // Remove dashes from phone before saving
    const phoneDigits = edited.phone ? edited.phone.replace(/\D/g, "") : "";

    this.membershipService
      .addOrUpdateMember(
        {
          MEMBER_NO: edited.customerNo || "",
          MEMBER_ID: edited.memberId || "",
          MEMBER_NM: edited.name,
          HP_NO: phoneDigits,
          SMS_YN: edited.smsYn || "Y",
        },
        this.auth.getUserId(),
        this.auth.getStrCd(),
      )
      .subscribe((res) => {
        if (res?.isOK) {
          this.masterService.showSuccess(
            this.translate.instant("SYSMESER0006"),
          );
          this.getData();
          this.showEdit = false;
          this.selectedRows.set([]);
        } else {
          this.masterService.showError(Constants.showError(res?.errorMessages));
        }
      });
  }

  onCancelEdit() {
    this.showEdit = false;
    this.selectedRows.set([]);
  }

  customerHeaders = computed<SortableHeader[]>(() => {
    return [
      { key: "select", label: "", sortable: false },
      {
        key: "customerNo",
        label: this.translate.instant("CUSTMSTR0029"),
        sortable: true,
      },
      {
        key: "name",
        label: this.translate.instant("CUSTMSTR0030"),
        sortable: true,
      },
      {
        key: "phone",
        label: this.translate.instant("CUSTMSTR0031"),
        sortable: true,
      },
      {
        key: "grade",
        label: this.translate.instant("CUSTMSTR0032"),
        sortable: true,
      },
      {
        key: "acc",
        label: this.translate.instant("CUSTMSTR0033"),
        sortable: true,
      },
      {
        key: "used",
        label: this.translate.instant("CUSTMSTR0035"),
        sortable: true,
      },
      {
        key: "avail",
        label: this.translate.instant("CUSTMSTR0036"),
        sortable: true,
      },
      {
        key: "count",
        label: this.translate.instant("CUSTMSTR0037"),
        sortable: true,
      },
      {
        key: "prepaid",
        label: this.translate.instant("CUSTMSTR0038"),
        sortable: true,
      },
      {
        key: "joinDate",
        label: this.translate.instant("CUSTMSTR0039"),
        sortable: true,
      },
      {
        key: "action",
        label: "",
        sortable: true
      },
    ];
  });

  prepaymentHeaders = computed<SortableHeader[]>(() => {
    return [
      // { key: "select", label: "", sortable: false },
      {
        key: "seqNo",
        label: this.translate.instant("CUSTMSTR0028"),
        sortable: true,
      },
      {
        key: "date",
        label: this.translate.instant("CUSTMSTR0040"),
        sortable: true,
      },
      {
        key: "receiptNo",
        label: this.translate.instant("CUSTMSTR0063"),
        sortable: true,
      },
      {
        key: "category",
        label: this.translate.instant("CUSTMSTR0041"),
        sortable: true,
      },
      {
        key: "topUpAmount",
        label: this.translate.instant("CUSTMSTR0042"),
        sortable: true,
      },
      {
        key: "balance",
        label: this.translate.instant("CUSTMSTR0043"),
        sortable: true,
      },
      {
        key: "cancellable",
        label: this.translate.instant("CUSTMSTR0044"),
        sortable: true,
      },
      { key: 'action', label: '', sortable: false },
    ];
  });

  usageHeaders = computed<SortableHeader[]>(() => {
    return [
      {
        key: "seqNo",
        label: this.translate.instant("CUSTMSTR0028"),
        sortable: true,
      },
      {
        key: "date",
        label: this.translate.instant("CUSTMSTR0040"),
        sortable: true,
      },
      {
        key: "receiptNo",
        label: this.translate.instant("CUSTMSTR0046"),
        sortable: true,
      },
      {
        key: "usedAmount",
        label: this.translate.instant("CUSTMSTR0047"),
        sortable: true,
      },
      {
        key: "balance",
        label: this.translate.instant("CUSTMSTR0048"),
        sortable: true,
      },
      {
        key: "category",
        label: this.translate.instant("CUSTMSTR0049"),
        sortable: true,
      },
    ];
  });

  // Methods for Customer Tab
  handleSaveNewMember() {
    // Validate phone is required
    if (!this.newMemberPhone()) {
      this.phoneError.set(
        this.translate
          .instant("SYSMESER0003")
          .replace("{{name}}", this.translate.instant("CUSTMSTR0018")),
      );
      return;
    }

    // Validate phone format
    const phone = this.newMemberPhone();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10 && digits.length !== 11) {
      this.phoneError.set(this.translate.instant("CUSTMSTR0052"));
      return;
    }

    // Format phone number for display check
    const formattedPhone = this.formatPhoneNumber(phone);
    if (!formattedPhone) {
      this.phoneError.set(this.translate.instant("CUSTMSTR0052"));
      return;
    }

    // Check for duplicate phone (compare with formatted version)
    // const existingMember = this.members().find(
    //   (m) => m.phone === formattedPhone,
    // );
    // if (existingMember) {
    //   this.phoneError.set(this.translate.instant("CUSTMSTR0053"));
    //   return;
    // }

    // Call API to save - send only digits to database
    this.membershipService
      .addOrUpdateMember(
        {
          MEMBER_NO: this.newMemberId(), // Empty for new customer
          MEMBER_ID: this.memberIdEdit(), // User input Member ID
          MEMBER_NM: this.newMemberName(),
          HP_NO: digits, // Save only digits without dashes
          SMS_YN: this.newMemberSms(),
        },
        this.auth.getUserId(),
        this.auth.getStrCd(),
      )
      .subscribe((res) => {
        if (res?.isOK) {
          const message = this.newMemberId().length > 0 ? 'SYSMESER0006' : 'CUSTMSTR0055';
          this.masterService.showSuccess(
            this.translate.instant(message),
          );
          this.getData();
          this.closeModal();
        } else {
          this.masterService.showError(Constants.showError(res?.errorMessages));
        }
      });
  }

  openModal() {
    this.newMemberName.set("");
    this.newMemberId.set("");
    this.newMemberPhone.set("");
    this.newMemberSms.set("Y");
    this.phoneError.set("");
    this.memberIdEdit.set("");
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.memberIdEdit.set("");
    this.isModalOpen.set(false);
  }

  toggleMember(member: Member, checked: boolean) {
    this.ontoggle.set(true);
    this.members.update(list =>
      list.map(x =>
        x.id === member.id ? { ...x, checked } : x
      )
    );
  }
  // }

  toggleSelectAll(event: any) {
    this.ontoggle.set(true);
    this.members.update(list =>
      list.map(x => ({ ...x, checked: event }))
    );
  }

  toggleRow(id: string) {
    this.selectedRows.update((rows) => {
      if (rows.includes(id)) {
        return rows.filter((r) => r !== id);
      } else {
        return [...rows, id];
      }
    });
  }

  // Format phone number to XXX-XXXX-XXXX (10 or 11 digits)
  formatPhoneNumber(phone: string): string | null {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      // 3-3-4
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    } else if (digits.length === 11) {
      // 3-4-4
      return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    }
    return null;
  }

  onPhoneInput(event: any) {
    const input = event.target as HTMLInputElement;
    const original = input.value;
    input.value = original.replace(/[eE\+\-]/g, '');
    input.value = input.value.replace(/[^0-9]/g, '');

    if (input.value.length <= 10) {
      input.value = input.value.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    } else {
      input.value = input.value.substring(0, 11);
      input.value = input.value.replace(/(\d{3})(\d{4})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    }

    this.newMemberPhone.set(input.value);
    this.phoneError.set("");
  }

  // Prepayment combobox methods
  onPhoneSearchInput(value: string) {
    this.prepaySearchPhone.set(value);

    if (!value || value.length < 2) {
      this.filteredMembersPhone.set([]);
      this.showPhoneDropdown.set(false);
      return;
    }

    // Filter members by phone
    const filtered = this.memberPrepayments().filter((m) => {
      return (
        m.phone.includes(value) || m.phone.replace(/-/g, "").includes(value)
      );
    });

    this.filteredMembersPhone.set(filtered);
    this.showPhoneDropdown.set(filtered.length > 0);
    this.prepaySearchPhone.set(value);
  }

  onNameSearchInput(value: string) {
    this.prepaySearchName.set(value);

    if (!value || value.length < 1) {
      this.filteredMembersName.set([]);
      this.showNameDropdown.set(false);
      return;
    }

    // Filter members by name
    const filtered = this.memberPrepayments().filter((m) => {

      const query = value.toLowerCase();
      return m.name?.toLowerCase().includes(query);
    });

    this.filteredMembersName.set(filtered);
    this.showNameDropdown.set(filtered.length > 0);
    this.prepaySearchName.set(value);
  }

  selectMemberFromPhoneDropdown(member: Member) {
    this.prepaySearchPhone.set(member.phone);
    this.prepaySearchName.set(member.name);
    this.showPhoneDropdown.set(false);
    this.filteredMembersPhone.set([]);
    this.searchPrepayCustomer();
  }

  selectMemberFromNameDropdown(member: Member) {
    this.prepaySearchPhone.set(member.phone);
    this.prepaySearchName.set(member.name);
    this.showNameDropdown.set(false);
    this.filteredMembersName.set([]);
    this.searchPrepayCustomer();
  }

  searchPrepayCustomer() {
    this.selectedPrepayCustomer.set(null)
    const phone = this.prepaySearchPhone();
    const name = this.prepaySearchName();

    if (!phone && !name) {
      this.masterService.showError(
        this.translate
          .instant("SYSMESER0003")
          .replace("{{name}}", "Phone or Name"),
      );
      return;
    }

    const member = this.memberPrepayments().find((m) => {
      const phoneMatch = phone
        ? m.phone === phone || m.phone?.includes(phone)
        : true;
      const nameMatch = name
        ? m.name === name || m.name?.toLowerCase().includes(name.toLowerCase())
        : true;
      return phoneMatch && nameMatch;
    });

    if (member) {
      this.selectedPrepayCustomer.set(member);
      this.prepaySearchPhone.set(member.phone);
      this.prepaySearchName.set(member.name);

      this.param1.P_HQ_CD = this.auth.getHQCD();
      this.param1.P_STR_CD = this.auth.getStrCd();
      this.param1.P_HP_NO = member.phone.replace(/\D/g, "");
      this.param1.P_MEMBER_NO = member.customerNo;

      this.param2.P_HQ_CD = this.auth.getHQCD();
      this.param2.P_STR_CD = this.auth.getStrCd();
      this.param2.P_HP_NO = member.phone.replace(/\D/g, "");
      this.param2.P_MEMBER_NO = member.customerNo;

      this.getDataPrepaymentHistory();
      this.getDataUsageHistory();
    } else {
      this.prepaymentHistory.set([]);
      this.usageHistory.set([]);
      this.selectedPrepayCustomer.set(null)
      this.prepayChargeAmount.set(0)
    }
  }

  clearPrepaySearch() {
    this.selectedPrepayCustomer.set(null);
    this.prepaySearchPhone.set("");
    this.prepaySearchName.set("");
    this.filteredMembersPhone.set([]);
    this.filteredMembersName.set([]);
    this.showPhoneDropdown.set(false);
    this.showNameDropdown.set(false);
    this.prepaymentHistory.set([]);
    this.usageHistory.set([]);
    this.prepayChargeAmount.set(0);
    this.prepayReturnAmount.set(0);
  }

  // Use helper functions for number formatting
  formatNumberWithCommas = formatNumberWithCommas;
  parseFormattedNumber = parseFormattedNumber;

  // Handle charge amount input change
  onChargeAmountChange(value: number) {
    this.prepayChargeAmount.set(value);
  }

  // Open payment method selector for top-up
  openTopUpPayment() {
    if (!this.selectedPrepayCustomer()) {
      this.masterService.showError(
        this.translate.instant("CUSTMSTR0068")
      );
      return;
    }

    const amount = this.prepayChargeAmount();
    if (amount <= 0) {
      this.masterService.showError(
        this.translate.instant("CUSTMSTR0057")
      );
      return;
    }

    this.showPaymentMethodSelector.set(true);
  }

  loading = signal(false);
  loadingText = signal('');
  setLoading(text: string) {
    this.loading.set(true);
    this.loadingText.set(text);
  }

  clearLoading() {
    this.loading.set(false);
    this.loadingText.set('');
  }
  // Handle payment completion (replaces onPaymentMethodSelected)
  onPaymentCompleted(result: any) {
    const customer = this.selectedPrepayCustomer();
    if (!customer) {
      this.masterService.showError(this.translate.instant("CUSTMSTR0068"));
      return;
    }

    this.selectedPaymentMethod.set(result.method);

    const paymentData = {
      memberNo: customer.customerNo,
      amount: result.amount,
      paymentMethod: result.method,
      installments: result.installments || 0,
      receivedAmount: result.receivedAmount || result.amount,
      additionalData: {
        PAYMENT_TYPE: result.type || result.method, // Cash/CashBill type
        HP_NO: customer.phone.replace(/\D/g, ''),
        MEMBER_NM: customer.name
      }
    };
    // this.membershipService
    //   .processPrepaidPayment(
    //     paymentData,
    //     this.auth.getUserId(),
    //     this.auth.getStrCd()
    //   )
    //   .subscribe({
    //     next: (res: any) => {
    //       if (res?.isOK) {
    //         this.paySuccess.set(true);
    //       }
    //       else {
    //         var errCode = res?.errorMessages[0]?.errorCode || res?.ErrorMessages[0]?.ErrorCode;
    //         if (errCode === "RECPTSTR0086") {
    //           this.paySuccess.set(true);
    //         }
    //         this.masterService.showError(Constants.showError(res?.errorMessages ?? res?.ErrorMessages))
    //       }
    //     },
    //     error: (err) => {
    //       this.masterService.showError(this.translate.instant('RECPTSTR0104'))
    //       // complete();
    //     }
    //   });
    // Call API to process payment
    this.membershipService
      .processPrepaidPayment(
        paymentData,
        this.auth.getUserId(),
        this.auth.getStrCd()
      )
      .subscribe(async (res) => {
        if (res?.isOK) {
          const paramWs = res?.result?.dataRes;
          const pendingId = res?.result?.dataRes?.pendingId
          if (result.method === this.paymentType.Card) {
            this.setLoading(this.translate.instant('SYSMESER0031'));
            try {
              const res = await this.webSocket.sendMessage(paramWs);
              const vanRespone = JSON.parse(res?.vanRespone);
              const message = `${vanRespone?.MSG_1}.${vanRespone?.MSG_2}`
              this.setLoading(this.translate.instant('SYSMESER0030'));
              this.ksNet.peymentChargeSuccessNoLoading(vanRespone, pendingId).then(async (res: any) => {
                if (res?.isOK) {
                  this.setLoading(this.translate.instant('SYSMESER0029'));
                  const paramPrintWs = res?.result?.dataRes;
                  try {
                    const res = await this.webSocket.sendMessage(paramPrintWs, 5000);
                    const printRespone = JSON.parse(res?.printRespone);
                    if (!printRespone?.Success) {
                      this.masterService.showError(this.translate.instant('RECPTSTR0086'))
                    }
                    this.paySuccess.set(true);
                    this.clearLoading();
                  } catch (err) {
                    this.clearLoading();
                    this.masterService.showError(this.translate.instant('AGENTSTR0001'))
                  }
                }
                else {
                  this.clearLoading();
                  this.masterService.showError(message)
                }
              })
            } catch (err) {
              this.clearLoading();
              this.masterService.showError(this.translate.instant('AGENTSTR0001'))
              // }
              // this.setLoading(this.translate.instant('SYSMESER0031'));
              // this.ksNet.payment(PrintBody, signature).subscribe({
              //   next: (res: any) => {
              //     const message = `${res?.msG_1}.${res?.msG_2}`
              //     this.setLoading(this.translate.instant('SYSMESER0030'));
              //     this.ksNet.paymentChargeSuccess(res, pendingId).subscribe((res: any) => {
              //       if (res?.isOK) {
              //         this.setLoading(this.translate.instant('SYSMESER0029'));
              //         const PrintBody2 = res?.result?.dataRes?.body;
              //         const signature2 = res?.result?.dataRes?.signature;
              //         this.ksNet.print(PrintBody2, signature2).subscribe({
              //           next: (res: any) => {
              //             this.clearLoading();
              //             this.paySuccess.set(true);
              //             // complete();
              //           },
              //           error: (err) => {
              //             this.paySuccess.set(true);
              //             this.clearLoading();
              //             this.masterService.showError(this.translate.instant('RECPTSTR0086'))
              //             // complete();
              //           }
              //         });
              //       }
              //       else {
              //         this.clearLoading();
              //         this.masterService.showError(this.translate.instant('RECPTSTR0103'))
              //         //this.masterService.showError(message);
              //       }
              //     })
              //   },
              //   error: (err) => {
              //     this.clearLoading();
              //     this.masterService.showError(this.translate.instant('RECPTSTR0102'))
              //   }
              // })
            }
          }
          else {
            this.setLoading(this.translate.instant('SYSMESER0029'));
            try {
              const res = await this.webSocket.sendMessage(paramWs, 5000);
              const printRespone = JSON.parse(res?.printRespone);
              if (!printRespone?.Success) {
                this.masterService.showError(this.translate.instant('RECPTSTR0086'))
              }
              this.paySuccess.set(true);
              this.clearLoading();
            } catch (err) {
              this.clearLoading();
              this.paySuccess.set(true);
              this.masterService.showError(this.translate.instant('AGENTSTR0001'))
            }
          }

        } else {
          this.masterService.showError(
            Constants.showError(res?.errorMessages) ||
            this.translate.instant('SYSMESER0001')
          );
        }
      });
  }

  onPaymentSuccess(result: any) {
    this.showPaymentMethodSelector.set(false);
    this.paySuccess.set(false);

    // Build success message with details
    const customer = this.selectedPrepayCustomer();
    // Reset form
    this.prepayChargeAmount.set(0);

    // Update customer balance in UI
    if (customer) {
      customer.prepaid = (customer.prepaid || 0) + result.amount;
    }

    // Refresh customer data and history
    this.getData();
    this.getDataPrepaymentHistory();
    this.getDataUsageHistory();
    // this.masterService.showSuccess(message);
  }

  onClickCancelTopup() {
    this.showConfirmCancel.set(false);
    const getData = () => {
      const customer = this.selectedPrepayCustomer();
      // Reset form
      this.prepayChargeAmount.set(0);

      // Update customer balance in UI
      if (customer) {
        customer.prepaid = (customer.prepaid || 0) - this.selectedChargeHistory().topUpAmount;
      }
      this.getData();
      this.getDataPrepaymentHistory();
      this.getDataUsageHistory();
      this.selectedChargeHistory.set(null)
    }

    // this.membershipService.CancelTopup(this.selectedChargeHistory()).subscribe({
    //   next: (res: any) => {
    //     if (!res?.isOK) {
    //       this.masterService.showError(Constants.showError(res?.errorMessages))
    //     }
    //     else {
    //       getData();
    //     }
    //   },
    //   error: (err) => {
    //     this.masterService.showError(this.translate.instant('RECPTSTR0088'))
    //   }
    // })

    const print = async (res: any) => {
      const paramWs = res?.result?.dataRes;
      this.setLoading(this.translate.instant('SYSMESER0029'));
      try {
        const res = await this.webSocket.sendMessage(paramWs, 5000);
        const printRespone = JSON.parse(res?.printRespone);
        if (!printRespone?.Success) {
          this.masterService.showError(this.translate.instant('RECPTSTR0086'))
        }
        this.clearLoading();
        getData();
        this.masterService.showSuccess(this.translate.instant('CUSTMSTR0059'))
      } catch (err) {
        this.clearLoading();
        this.masterService.showError(this.translate.instant('RECPTSTR0086'))
        this.masterService.showSuccess(this.translate.instant('CUSTMSTR0059'))
        getData();
      }
    }

    this.membershipService.CancelTopup(this.selectedChargeHistory()).subscribe(async (res: any) => {
      if (res?.isOK) {
        const paramWs = res?.result?.dataRes;
        const pendingId = res?.result?.dataRes?.pendingId
        if (pendingId) {
          this.setLoading(this.translate.instant('SYSMESER0031A'));
          const res = await this.webSocket.sendMessage(paramWs);
          const vanRespone = JSON.parse(res?.vanRespone);
          this.setLoading(this.translate.instant('SYSMESER0030A'));
          this.ksNet.cancelPrepaidSuccessNoLoading(vanRespone, pendingId)
            .then(async (res: any) => {
              if (res?.isOK) {
                // this.clearLoading();
                // this.masterService.showSuccess(this.translate.instant('RECPTSTR0084'))
                // getData();
                await print(res);
              }
              else {
                this.showConfirmCancel.set(false);
                this.clearLoading();
                this.masterService.showError(this.translate.instant('RECPTSTR0088'))
              }
            })

          //     const message = `${vanRespone?.msG_1}.${vanRespone?.msG_2}`
          // this.ksNet.cancel(Body, signature).subscribe({
          //   next: (res: any) => {
          //     this.setLoading(this.translate.instant('SYSMESER0030A'));

          //     this.ksNet.cancelPrepaidSuccess(res, pendingId).subscribe((res: any) => {
          //       if (res?.isOK) {
          //         this.clearLoading();
          //         this.masterService.showSuccess(this.translate.instant('RECPTSTR0084'))
          //         getData();
          //       }
          //       else {
          //         this.showConfirmCancel.set(false);
          //         this.clearLoading();
          //         this.masterService.showError(this.translate.instant('RECPTSTR0088'))
          //       }
          //     })
          //   },
          //   error: (err) => {
          //     this.showConfirmCancel.set(false);
          //     this.clearLoading();
          //     this.masterService.showError(this.translate.instant('RECPTSTR0088'))
          //   }
          // })
        }
        else {
          // this.clearLoading();
          // getData();
          // this.masterService.showSuccess(this.translate.instant('CUSTMSTR0059'));
          await print(res);
        }
      }
      else {
        this.showConfirmCancel.set(false);
        this.clearLoading();
        this.masterService.showError(Constants.showError(res?.errorMessages));
      }
    })
  }

  // Cancel payment method selection
  onPaymentMethodCancelled() {
    this.showPaymentMethodSelector.set(false);
  }

  onPhoneInputSearch(event: Event, name: 'P_HP_NO' | 'prepaySearchPhone') {
    const input = event.target as HTMLInputElement;
    const original = input.value;
    input.value = original.replace(/[eE\+\-]/g, '');
    input.value = input.value.replace(/[^0-9]/g, '');

    if (input.value.length <= 10) {
      input.value = input.value.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    } else {
      input.value = input.value.substring(0, 11);
      input.value = input.value.replace(/(\d{3})(\d{4})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    }

    if (name == "P_HP_NO") this.param.P_HP_NO = input.value;
    else this.prepaySearchPhone.set(input.value)
  }
}
